import { timeAgo, type ActivityRow, type SupplierInsight, t, intlLocale } from '@procumi/shared';
import { CardHead, I, PATHS, pct, Section } from './shared';

/* ------------------------------------------------------------ furnizorii */

const POSITION: Record<SupplierInsight['position'], { text: string; tone: string }> = {
  strong: { text: t('Competitiv'), tone: 'green' },
  average: { text: t('Uneori cel mai bun'), tone: 'amber' },
  weak: { text: t('Rar cel mai bun'), tone: 'gray' },
  new: { text: t('Fără comparație'), tone: 'gray' },
};

const days = (d: number) => `${d.toLocaleString(intlLocale())} ${d === 1 ? t('zi') : t('zile')}`;

/**
 * Furnizorii, ca informație de cumpărare (nu agendă): patru repere — cine
 * răspunde cel mai des, cine cel mai repede, cine e cel mai des cel mai ieftin,
 * cine trebuie urmărit — și lista scurtă dedesubt.
 */
export function SupplierPerformance({ rows, onAll }: { rows: SupplierInsight[]; onAll: () => void }) {
  const asked = rows.filter((s) => s.asked >= 1);
  const best = <T,>(list: T[], score: (x: T) => number) =>
    list.length ? list.reduce((a, b) => (score(b) > score(a) ? b : a)) : null;
  const rate = best(asked, (s) => s.rate + s.offers / 1000);
  const fast = best(
    asked.filter((s) => s.responseDays != null),
    (s) => -s.responseDays!,
  );
  const comp = best(
    asked.filter((s) => s.compared),
    (s) => s.cheapest / s.compared + s.compared / 1000,
  );
  const watch = best(
    asked.filter((s) => s.rate < 0.75),
    (s) => -s.rate + s.asked / 1000,
  );
  const marks: { label: string; s: SupplierInsight | null; v: string; tone?: string }[] = [
    { label: t('Răspunde cel mai des'), s: rate, v: rate ? pct(rate.rate) : '' },
    { label: t('Cel mai rapid'), s: fast, v: fast?.responseDays != null ? days(fast.responseDays) : '' },
    {
      label: t('Cel mai des cel mai ieftin'),
      s: comp,
      v: comp ? t('{0} din {1}', comp.cheapest, comp.compared) : '',
    },
    { label: t('De urmărit'), s: watch, v: watch ? t('răspunde {0}', pct(watch.rate)) : '', tone: 'amber' },
  ];
  return (
    <Section
      icon="people"
      tone="blue"
      title={t('Cum lucrează furnizorii')}
      sub={t('Din cererile trimise: răspunsuri, viteză, cel mai bun preț')}
      className="db-sup"
      action={
        <button type="button" className="link" onClick={onAll}>
          {t('Analiza furnizorilor →')}
        </button>
      }
    >
      {rows.length ? (
        <>
          <dl className="db-marks">
            {marks.map((m) => (
              <div key={m.label}>
                <dt>{m.label}</dt>
                <dd>
                  {m.s ? (
                    <>
                      <b>{m.s.supplier}</b>
                      <span className={m.tone}>{m.v}</span>
                    </>
                  ) : (
                    <span className="muted">—</span>
                  )}
                </dd>
              </div>
            ))}
          </dl>
          <ul className="db-sup-l">
            {rows.slice(0, 5).map((s) => (
              <li key={s.supplierId}>
                <b>{s.supplier}</b>
                <span className="db-rate" title={t('{0} oferte din {1} cereri', s.offers, s.asked)}>
                  <span className="db-rate-t">
                    <span style={{ width: pct(s.rate) }} className={s.rate < 0.5 ? 'low' : undefined} />
                  </span>
                  {pct(s.rate)}
                </span>
                <span className="db-sup-d">{s.responseDays == null ? '—' : days(s.responseDays)}</span>
                <span
                  className={`db-st ${POSITION[s.position].tone}`}
                  title={
                    s.compared
                      ? t('cel mai ieftin în {0} din {1} comparații', s.cheapest, s.compared)
                      : undefined
                  }
                >
                  {POSITION[s.position].text}
                </span>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <div className="db-empty">{t('Nicio cerere trimisă furnizorilor în perioadă.')}</div>
      )}
    </Section>
  );
}

/* ------------------------------------------------------------ activitatea */

/** Iconița și culoarea fiecărui fel de eveniment, în culorile mărcii; roșu doar la refuz și anulare, chihlimbar la ce așteaptă. */
const KIND: Record<ActivityRow['kind'], [keyof typeof PATHS, string]> = {
  rfq_created: ['rfq', 'blue'],
  rfq_sent: ['send', 'blue'],
  offer_received: ['inbox', 'night'],
  supplier_chosen: ['star', 'blue'],
  order_issued: ['box', 'night'],
  supplement_issued: ['box', 'night'],
  order_pending: ['clock', 'amber'],
  order_approved: ['check', 'blue'],
  order_rejected: ['x', 'red'],
  order_canceled: ['x', 'red'],
  order_completed: ['check', 'night'],
  delivery_in: ['truck', 'blue'],
  delivery_return: ['truck', 'night'],
  rfq_closed: ['check', 'gray'],
  rfq_canceled: ['x', 'red'],
  requisition_created: ['clipboard', 'slate'],
  requisition_edited: ['clipboard', 'slate'],
  requisition_rejected: ['x', 'red'],
  rfq_commented: ['chat', 'gray'],
};

/** Activitatea, ca linie de timp scurtă, pe două coloane pe ecran lat. */
export function RecentActivity({
  rows,
  now,
  onOpen,
  onAll,
}: {
  rows: ActivityRow[];
  now: number;
  onOpen: (r: ActivityRow) => void;
  onAll: () => void;
}) {
  return (
    <section className="db-card db-act">
      <CardHead
        icon="clock"
        tone="slate"
        title={t('Activitate recentă')}
        sub={t('Ce s-a întâmplat în cereri, comenzi și livrări')}
        action={
          <button type="button" className="link" onClick={onAll}>
            {t('Toată activitatea →')}
          </button>
        }
      />
      {rows.length ? (
        <ol className="db-feed">
          {rows.map((r) => (
            <li key={r.key}>
              <span className={`db-ico ${KIND[r.kind]?.[1] ?? 'gray'}`}>
                <I d={KIND[r.kind]?.[0] ?? 'clock'} size={16} />
              </span>
              <button type="button" disabled={!r.rfqId} onClick={() => onOpen(r)}>
                <b>{t(r.text)}</b>
                <span>
                  {timeAgo(r.at, now)}
                  {r.actor ? ` · ${r.actor}` : ''}
                </span>
              </button>
            </li>
          ))}
        </ol>
      ) : (
        <div className="db-empty">
          {t('Aici apar ofertele primite, comenzile, livrările și cererile noi.')}
        </div>
      )}
    </section>
  );
}
